import type { ReactNode } from 'react';
import { Button } from '@/shared/ui/button';
import { styles } from '@/shared/app-stepper-form/styles';

/**
 * The pinned footer: Cancel, Back, and either Continue or the submit action.
 *
 * WHY IT IS PINNED
 * ----------------
 * The primary action must be in the same place on every step and must never
 * require scrolling to reach. In the previous layout the navigation sat BELOW
 * the panel in the page's scroll flow, so a long step pushed "Continue" past the
 * fold — the operator had to scroll to find the control that moves them forward.
 *
 * WHY CANCEL IS SEPARATED
 * -----------------------
 * Cancel leaves the flow entirely; Back moves within it. Putting them side by
 * side would make "go back one step" and "throw the whole thing away" adjacent
 * targets, which is exactly the pair a user must never confuse. Cancel sits on
 * the far left, away from the primary action.
 *
 * Cancel is OUTLINED, not filled. It used to be `danger`, on the reasoning that
 * it is the one control that destroys work. That reasoning was wrong in
 * practice: a filled red button on every step of a four-step form is the loudest
 * thing on the screen, and it competes with the primary action for attention on
 * every single step — including the three where the user has typed nothing worth
 * losing. The destructive moment is not the click, it is the CONFIRMATION, and
 * that dialog already carries the danger styling. Cancel itself is a way out,
 * not a threat, so it reads as a quiet outlined control and the red is spent
 * where it means something.
 *
 * `Back` is HIDDEN on the first step rather than disabled. There is nowhere to go
 * back to, so a disabled button would be a control that can never be used — and
 * on step 1 it sat directly beside Cancel, which is the worst possible pairing
 * for a control that does nothing. It is rendered from step 2 onward, where it
 * has a destination.
 *
 * CONTINUE IS GATED ON THE STEP'S OWN VALIDITY. A Continue button that looks
 * live and silently refuses the click is the single most common way a stepper
 * feels broken: the user presses it, nothing happens, and nothing on screen says
 * which field is holding them back. Disabling it — together with the "Required"
 * marker beside each mandatory field — turns a dead end into an instruction.
 */
export function StepNavigation({
  isFirst,
  isLast,
  canAdvance,
  isSubmitting,
  submitLabel,
  cancelLabel,
  onCancel,
  onBack,
  onAdvance,
  onSubmit,
}: {
  isFirst: boolean;
  isLast: boolean;
  canAdvance: boolean;
  isSubmitting: boolean;
  submitLabel: string;
  cancelLabel: string;
  onCancel: () => void;
  onBack: () => void;
  onAdvance: () => void;
  onSubmit: () => void;
}): ReactNode {
  return (
    <div className={styles.panelFooter}>
      <div className={styles.nav}>
        <Button variant="secondary" onClick={onCancel}>
          {cancelLabel}
        </Button>
        {isFirst ? null : (
          <Button variant="ghost" onClick={onBack}>
            Back
          </Button>
        )}
      </div>
      <div className={styles.nav}>
        {isLast ? (
          <Button loading={isSubmitting} onClick={onSubmit}>
            {submitLabel}
          </Button>
        ) : (
          <Button disabled={!canAdvance} onClick={onAdvance}>
            Continue
          </Button>
        )}
      </div>
    </div>
  );
}
